import {
  Component,
  OnChanges,
  Input,
  ViewChild,
  ElementRef,
  HostListener,
  OnDestroy,
  SimpleChanges,
  Output,
  EventEmitter,
  OnInit,
} from '@angular/core';
import { SubscriptionLike, merge, Subscription } from 'rxjs';
import { map, filter } from 'rxjs/operators';
import { RowNode } from 'ag-grid-community';
import { BatteryStatusPipe, XsI18nPipe } from '@nx-fe/pipes';
import { GlobalEventBusService, IDeviceModel, IUserInfoModel, UserContextService } from '@nx-fe/services';
import { GlobalEvents } from 'app/core/global-event-constants';
import {
  SnapshotSection,
  NavbarItems,
  SnapshotService,
  SnapshotModel,
  SnapshotSaveStatus,
} from 'app/modules/ticket/services/snapshot.service';
import { Toast, ToastTypeIndex } from 'app/shared/components/toast/toast.component';
import { DeviceCheckinStateService } from 'app/modules/signalr/broadcasters/checkin.state.service';
import { DeviceCheckinCompleted, DeviceCheckinResultCode } from 'app/modules/signalr/models/device-checkin-completed';
import { DeviceCheckinFailure } from 'app/modules/signalr/models/device-checkin-failure';
import { cloneDeep } from 'lodash';
import { KeyValue } from '@angular/common';
import { XSightChartColors } from '../../../../../../../../../../assets/theme/colors.theme';

export enum KeyNames {
  home = 'Home',
  end = 'End',
  pageUp = 'PageUp',
  pageDown = 'PageDown',
}

@Component({
  selector: 'xs-device-info-view',
  templateUrl: './device-info-view.component.html',
  styleUrls: ['./device-info-view.component.scss'],
})
export class DeviceInfoViewComponent implements OnChanges, OnInit, OnDestroy {
  @Input() device: IDeviceModel;
  @Input() activityId: number;
  @Input() isLive: boolean;
  @Input() titleHeight: number;

  @ViewChild('device_details', { static: true }) device_details: ElementRef<HTMLDivElement>;
  @ViewChild('device_configurations', { static: false }) device_configurations: ElementRef<HTMLDivElement>;
  @ViewChild('device_application', { static: false }) device_application: ElementRef<HTMLDivElement>;
  @ViewChild('device_security', { static: false }) device_security: ElementRef<HTMLDivElement>;
  @ViewChild('device_content', { static: false }) device_content: ElementRef<HTMLDivElement>;
  @ViewChild('device_notes', { static: false }) device_notes: ElementRef<HTMLDivElement>;
  @ViewChild('device_info_container', { static: false }) device_info_container: ElementRef<HTMLDivElement>;
  @ViewChild('device_info_details_container', { static: false }) device_details_container: ElementRef<HTMLDivElement>;
  // @ViewChildren(DynamicTableComponent) dynamicTables !: QueryList<DynamicTableComponent>;

  @Output() setToast = new EventEmitter<Toast>();
  @Output() snapshotRefreshInProgress = new EventEmitter<boolean>();

  snapshotDeviceDetails: { [sectionId: string]: SnapshotSection };
  // To store the height of the highlight zone
  highlightZoneHeight = 0;
  deviceDetailBasicProperties: SnapshotSection;
  snapshotConfigurations: { [sectionId: string]: SnapshotSection };
  snapshotInstalledApplications: SnapshotSection;
  snapshotProfiles: SnapshotSection;
  snapshotNotes: SnapshotSection;
  snapshotSecurity: SnapshotSection;
  snapshotContent: SnapshotSection;
  snapshotProvisioningProfiles: SnapshotSection;
  isLoaded = false;
  refreshInProgress = false;
  smallInfographics = false;

  private scrollPos = 0;
  private snapShotSubscription: SubscriptionLike;
  private enableHighlight = false;
  private highlightClicked = false;
  private focusInContainer = false;
  private rcPopoutWindowTransferSub: Subscription;
  private rcLiveSnapshotSub: Subscription;
  private savedElementInView = '';
  disableScrollTracking = false;

  @HostListener('window:resize', ['$event'])
  onResize() {
    this.calculateContainerHeight();
    this.checkInfographicsSizing(this.device_details);
  }

  @HostListener('document:click', ['$event'])
  onClick(event: MouseEvent) {
    const isSnapshotMenuBarItem = (event.target as Element).closest('xs-snapshot-buttons');
    const isSnapshotNavigationMenuItem = this.elementRef.nativeElement.contains(event.target as Element);
    if (isSnapshotNavigationMenuItem || isSnapshotMenuBarItem) {
      this.focusInContainer = true;
      (<HTMLElement>this.elementRef.nativeElement.firstElementChild).focus();
      this.calculateContainerHeight();
    } else {
      this.focusInContainer = false;
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (this.focusInContainer) {
      switch (event.key) {
        case KeyNames.home: {
          this.elementInView = NavbarItems[NavbarItems.device_details];
          this.scrollToView(this.device_details.nativeElement);
          break;
        }
        case KeyNames.end: {
          this.elementInView = NavbarItems[NavbarItems.device_notes];
          this.scrollToView(this.device_notes.nativeElement);
          break;
        }
      }
    }
  }

  @HostListener('document:keyup', ['$event'])
  handleKeyUp(event: KeyboardEvent): void {
    if (this.focusInContainer) {
      switch (event.key) {
        case KeyNames.pageUp: {
          setTimeout(() => {
            this.scrollHandler();
          }, 150);
          break;
        }
        case KeyNames.pageDown: {
          setTimeout(() => {
            this.scrollHandler();
          }, 150);
          break;
        }
      }
    }
  }

  @HostListener('window:scroll', ['$event'])
  scrollHandler() {
    if (this.disableScrollTracking) {
      return;
    }
    if (this.highlightClicked) {
      this.highlightClicked = false;
      this.scrollPos = this.device_details.nativeElement.getBoundingClientRect().top;
      return;
    }

    if (this.device_details.nativeElement.getBoundingClientRect().top > this.scrollPos) {
      // Scroll Up
      if (this.checkOffsetOnScrollUp(this.device_details)) {
        this.enableHighlight = true;
        this.elementInView = NavbarItems[NavbarItems.device_details];
      } else if (this.checkOffsetOnScrollUp(this.device_configurations)) {
        this.enableHighlight = true;
        this.elementInView = NavbarItems[NavbarItems.device_configurations];
      } else if (this.checkOffsetOnScrollUp(this.device_application)) {
        this.enableHighlight = true;
        this.elementInView = NavbarItems[NavbarItems.device_application];
      } else if (this.checkOffsetOnScrollUp(this.device_notes)) {
        this.enableHighlight = true;
        this.elementInView = NavbarItems[NavbarItems.device_notes];
      } else if (this.checkOffsetOnScrollUp(this.device_security)) {
        this.enableHighlight = true;
        this.elementInView = NavbarItems[NavbarItems.device_security];
      } else if (this.checkOffsetOnScrollUp(this.device_content)) {
        this.enableHighlight = true;
        this.elementInView = NavbarItems[NavbarItems.device_content];
      }
    } else {
      // Scroll Down
      // Check the header position of each category to highlight
      if (this.checkOffsetOnScroll(this.device_details)) {
        this.elementInView = NavbarItems[NavbarItems.device_details];
      } else if (this.checkOffsetOnScroll(this.device_configurations)) {
        this.elementInView = NavbarItems[NavbarItems.device_configurations];
      } else if (this.checkOffsetOnScroll(this.device_application)) {
        this.elementInView = NavbarItems[NavbarItems.device_application];
      } else if (this.checkOffsetOnScroll(this.device_notes)) {
        this.elementInView = NavbarItems[NavbarItems.device_notes];
      } else if (this.checkOffsetOnScroll(this.device_security)) {
        this.elementInView = NavbarItems[NavbarItems.device_security];
      } else if (this.checkOffsetOnScroll(this.device_content)) {
        this.elementInView = NavbarItems[NavbarItems.device_content];
      }
    }

    // saves the new position for iteration.
    this.scrollPos = this.device_details.nativeElement.getBoundingClientRect().top;
  }

  constructor(
    private snapshotService: SnapshotService,
    private elementRef: ElementRef<HTMLDivElement>,
    private globalEventBusService: GlobalEventBusService,
    private saUserContextService: UserContextService,
    private saI18nPipe: XsI18nPipe,
    private deviceCheckinStateService: DeviceCheckinStateService,
    private pwrPipe: BatteryStatusPipe
  ) {
    this.setupCheckinRequestSubscriptions();
  }

  /**
   * for live snapshot we pass in device model
   * for saved/existing snapshot we pass in snapshot id
   */
  ngOnChanges(changes: SimpleChanges): void {
    if (changes && changes.device && changes.device.currentValue && this.isLive) {
      // live snapshot
      this.device = changes.device.currentValue;
      const snapshotMetadata = this.snapshotService.getSnapshotMetaData(this.device.deviceId);

      if (!snapshotMetadata || !snapshotMetadata.activityId) {
        this.getDeviceSnapshot();
      } else {
        // setTimeout(() => {
        this.getExistingSnapshot(snapshotMetadata.activityId);
        // });
      }
    } else if (changes && changes.activityId && changes.activityId.currentValue != null && !this.isLive) {
      // settimeout used to resolve issue with modifying fetchingSnapshot durring on changes.
      // To resolve this issue remove the old snapshot buttons from remote-control-view.component.html
      // setTimeout(() => {
      this.getExistingSnapshot(changes.activityId.currentValue);
      // }, 500);
    }
    this.checkInfographicsSizing(this.device_details);
  }

  ngOnInit(): void {
    // When popping in scroll to the item selected
    this.rcLiveSnapshotSub = this.globalEventBusService.register(GlobalEvents.viewTicketActivePanels, () =>
      setTimeout(() => {
        this.onResize();
        this.tryScrollToSelectedView();
      }, 0)
    );
  }

  ngOnDestroy(): void {
    if (this.snapShotSubscription) {
      this.snapShotSubscription.unsubscribe();
    }
    if (this.device && this.device.deviceId) {
      this.snapshotService.resetCurrentSnapshotPageView(this.device.deviceId);
    }

    if (this.rcPopoutWindowTransferSub) {
      this.rcPopoutWindowTransferSub.unsubscribe();
    }

    if (this.rcLiveSnapshotSub) {
      this.rcLiveSnapshotSub.unsubscribe();
    }
  }

  get elementInView() {
    if (this.device?.deviceId && this.isLive) {
      this.savedElementInView = this.snapshotService.getElementInView(this.device.deviceId);
    }

    return this.savedElementInView;
  }

  set elementInView(elementName) {
    if (this.device?.deviceId && this.isLive) {
      this.snapshotService.setElementInView(this.device.deviceId, elementName);
    }

    this.savedElementInView = elementName;
  }

  resizeUi() {
    // XTODO:
    // this.dynamicTables.forEach((dt) => {
    //     dt.onGridSizeChanged(null);
    // });
    this.calculateContainerHeight();
    this.checkInfographicsSizing(this.device_details);
  }

  scrollToView(elementId: HTMLElement): void {
    this.focusInContainer = true;
    this.highlightClicked = true;
    this.device_info_container.nativeElement.scrollTop = elementId.offsetTop;
    this.elementInView = elementId.id;
    this.enableHighlight = false;
  }

  scrollToTop = (): void => {
    setTimeout(() => {
      this.scrollToView(this.device_details.nativeElement);
    });
  };

  highlightIcon(elementName: string) {
    return elementName === this.elementInView;
  }

  isCustomAttributes(section: KeyValue<string, SnapshotSection>) {
    if (section.key === 'CustomAttributes') {
      return true;
    }

    return false;
  }

  hasRowData(section: KeyValue<string, SnapshotSection>) {
    if (section.value && section.value['rows'] && section.value['rows'].length > 0) {
      return true;
    }

    return false;
  }

  onRefreshSnapshotPressed() {
    if (this.device && this.device.deviceId) {
      this.setRefreshState(true);
      this.displayToastMessage('snapshot_getting_latest_info_message', ToastTypeIndex.info);
      this.deviceCheckinStateService.startCheckinInProgress(this.device.deviceId);
    }
  }

  getForeColor(type: string) {
    switch (type) {
      case 'device':
        return XSightChartColors.getColor('--device-infographics-battery-fore-color');
      case 'memory':
        return XSightChartColors.getColor('--device-infographics-memory-fore-color');
      case 'storage':
        return XSightChartColors.getColor('--device-infographics-storage-fore-color');
      default:
        break;
    }
  }

  getBackColor(type: string) {
    switch (type) {
      case 'device':
        return XSightChartColors.getColor('--device-infographics-battery-back-color');
      case 'memory':
        return XSightChartColors.getColor('--device-infographics-memory-back-color');
      case 'storage':
        return XSightChartColors.getColor('--device-infographics-storage-back-color');
      default:
        break;
    }
  }

  tryScrollToSelectedView(): void {
    this.delayScrollTracking();

    const elementName = this.elementInView;
    if (!elementName) {
      this.scrollToTop();
      return;
    }

    let elementRef: ElementRef;

    switch (elementName) {
      case 'device_details':
        elementRef = this.device_details;
        break;

      case 'device_configurations':
        elementRef = this.device_configurations;
        break;

      case 'device_application':
        elementRef = this.device_application;
        break;

      case 'device_security':
        elementRef = this.device_security;
        break;

      case 'device_content':
        elementRef = this.device_content;
        break;

      case 'device_notes':
        elementRef = this.device_notes;
        break;

      default:
        return;
    }

    setTimeout(() => {
      this.scrollToView(elementRef.nativeElement);
    });
  }

  delayScrollTracking = (): void => {
    // renable scrolling in quarter second so does not scroll back to old item. Fixes firefox scrolling issue
    this.disableScrollTracking = true;
    setTimeout(() => {
      this.disableScrollTracking = false;
    }, 250);
  };

  private setRefreshState = (inProgress: boolean) => {
    this.refreshInProgress = inProgress;
    this.snapshotRefreshInProgress.emit(inProgress);
  };

  setupCheckinRequestSubscriptions() {
    const isWatchedDevice = (msg: DeviceCheckinCompleted | DeviceCheckinFailure, target: IDeviceModel) =>
      msg && target && msg.deviceId === target.deviceId;

    const completionObservable = this.deviceCheckinStateService.deviceCheckinCompleted$.pipe(
      map((x: DeviceCheckinCompleted): Toast => {
        let toast = null;
        if (isWatchedDevice(x, this.device) && this.isLive) {
          switch (x.result) {
            case DeviceCheckinResultCode.CheckinSuccessfull:
              if (this.refreshInProgress) {
                this.getDeviceSnapshot();
              }
              break;
            case DeviceCheckinResultCode.CheckinTimeout:
              this.setRefreshState(false);
              toast = { message: 'snapshot_getting_latest_failed_message', type: ToastTypeIndex.error };
              break;
          }
        }
        return toast;
      })
    );

    const errorObservable = this.deviceCheckinStateService.deviceCheckinError$.pipe(
      map((x: DeviceCheckinFailure): Toast => {
        let toast = null;
        this.setRefreshState(false);
        if (isWatchedDevice(x, this.device)) {
          toast = {
            message: 'snapshot_checkin_request_error_message',
            type: ToastTypeIndex.error,
          };
        }
        return toast;
      })
    );

    merge(completionObservable, errorObservable)
      .pipe(filter(val => val !== null))
      .subscribe(x => {
        this.displayToastMessage(x.message, x.type);
      });
  }

  isSectionSupported = (snapshotSection: SnapshotSection): boolean => {
    let isSupported = false;
    if (snapshotSection) {
      isSupported = snapshotSection.isSupported;
    }
    return isSupported;
  };

  private displayToastMessage = (messageKey: string, type: ToastTypeIndex) => {
    this.setToast.emit({ message: this.saI18nPipe.transform(messageKey), type } as Toast);
  };

  private getDeviceSnapshot() {
    if (this.snapshotService.fetchingSnapshot) {
      return;
    }
    this.snapshotService.fetchingSnapshot = true;
    this.isLoaded = false;
    this.snapshotService.clearElementInViewState(this.device.deviceId);
    this.snapShotSubscription = this.snapshotService.getDeviceSnapshot(this.device).subscribe(
      (data: SnapshotModel) => {
        this.processSnapshotData(data);
        this.setSnapshotInfo(
          this.device.deviceId,
          data.activityId,
          data.snapshotDate,
          SnapshotSaveStatus.Unsaved,
          this.elementInView
        );
        this.snapshotService.fetchingSnapshot = false;
        this.tryScrollToSelectedView();
      },
      (error: any) => {
        if (error) {
          console.error(error);
          this.snapshotService.fetchingSnapshot = false;
          setTimeout(() => {
            if (this.refreshInProgress) {
              this.setRefreshState(false);
              this.displayToastMessage('snapshot_refresh_error_message', ToastTypeIndex.error);
            }
          });
        }
      }
    );
  }

  private getExistingSnapshot = (activityId: number) => {
    console.log('getExistingSnapshot', activityId);
    this.snapshotService.fetchingSnapshot = true;
    this.isLoaded = false;
    if (!this.isLive) {
      this.savedElementInView = '';
    }
    this.snapShotSubscription = this.snapshotService.getSavedDeviceSnapshot(activityId).subscribe(
      (data: SnapshotModel) => {
        this.processSnapshotData(data);

        const deviceId = this.device ? this.device.deviceId : data.deviceId;
        // if we are displaying a preview, we don't want to overwrite the snapshot information
        if (this.isLive) {
          const saveStatus = data.isSaved ? SnapshotSaveStatus.Saved : SnapshotSaveStatus.Unsaved;
          this.setSnapshotInfo(deviceId, data.activityId, data.snapshotDate, saveStatus, this.elementInView);
        }
        this.snapshotService.fetchingSnapshot = false;
        this.tryScrollToSelectedView();
      },
      (error: any) => {
        if (error) {
          console.error(error);
          this.snapshotService.fetchingSnapshot = false;
        }
      }
    );
  };

  private processSnapshotData = (snapshotData: SnapshotModel) => {
    const data = cloneDeep(snapshotData);
    this.isLoaded = true;
    // read and process data
    if (data.sections.Details && data.sections.Details.sections) {
      this.snapshotDeviceDetails = { ...data.sections.Details.sections, Details: undefined };
      delete this.snapshotDeviceDetails.Details;
      this.deviceDetailBasicProperties = { ...data.sections.Details.sections.Details };
      
      // Process BatteryStatus for special values
      if (this.snapshotDeviceDetails?.HardwareDetails?.properties?.BatteryStatus != null) {
        const batteryStatusValue = this.snapshotDeviceDetails.HardwareDetails.properties.BatteryStatus.value;
        this.snapshotDeviceDetails.HardwareDetails.properties.BatteryStatus.value = this.saI18nPipe.transform(this.pwrPipe.transform(batteryStatusValue), batteryStatusValue);
      }
    }

    // Force Device details into separate section that shows at top
    if (data.sections.Configuration && data.sections.Configuration.sections) {
      this.snapshotConfigurations = data.sections.Configuration.sections;
    }

    if (data.sections.Applications && data.sections.Applications.sections) {
      if (data.sections.Applications.sections.Applications) {
        this.snapshotInstalledApplications = data.sections.Applications.sections.Applications;
        // in the multi MC environments with the SI the applications is 3 levels deep.
        // Handle that gracefully in the front end as well as null rows
        if (this.snapshotInstalledApplications.sections?.Applications) {
          this.snapshotInstalledApplications = this.snapshotInstalledApplications.sections.Applications;
        }

        this.snapshotInstalledApplications.rows?.forEach(app => {
          app.Status = this.saI18nPipe.transform(app.Status);
        });
      }
      if (data.sections.Applications.sections.ProvisioningProfiles) {
        this.snapshotProvisioningProfiles = data.sections.Applications.sections.ProvisioningProfiles;
      }
    }

    this.snapshotNotes = this.populateUserNameForNotes(data.sections.Notes);

    data.sections.Security?.sections.Certificates.rows?.forEach(app => {
      app.Status = this.saI18nPipe.transform(app.Status);
    });

    this.snapshotSecurity = data.sections.Security?.sections;

    this.snapshotContent = data.sections.ContentFiles;

    this.snapshotProfiles = this.mapProfileRows(data.sections.Configuration.sections.Profiles);

    if (!this.elementInView) {
      this.elementInView = NavbarItems[NavbarItems.device_details];
    }
    setTimeout(() => {
      this.resizeUi();
      if (this.refreshInProgress) {
        this.setRefreshState(false);
        this.displayToastMessage('snapshot_getting_latest_success_message', ToastTypeIndex.success);
      }
    });
  };

  private setSnapshotInfo = (
    deviceId: string,
    activityId: number,
    snapshotDate: Date,
    saveStatus: SnapshotSaveStatus,
    currentPage: string
  ) => {
    this.snapshotService.setSnapshotMetaData(deviceId, activityId, snapshotDate, saveStatus, currentPage);
  };

  private mapProfileRows(profiles: SnapshotSection) {
    if (profiles && profiles.rows) {
      profiles.rows.forEach(row => {
        if (row.RowType === 'ConfigurationRow') {
          row.Name = row.Name ? row.ConfigurationType + ' - ' + row.Name : row.ConfigurationType;
        }
      });
    }
    return profiles;
  }

  isProfileRowGroupHeader(node: RowNode) {
    return node && node.data && node.data.RowType === 'ProfileRow';
  }

  private populateUserNameForNotes = (notes: SnapshotSection) => {
    if (notes && notes.rows) {
      const usersInfoIdsList = notes.rows.map(note => note.UserInfoPublicId).filter(x => !!x);

      if (usersInfoIdsList) {
        const userInfosPromise = this.saUserContextService.getUserInfoList(usersInfoIdsList);

        userInfosPromise.subscribe((resolvedUsersList: IUserInfoModel[]) => {
          const userLookupDictionary: { [userinfoId: string]: string } = {};

          resolvedUsersList.forEach((resolvedUser: IUserInfoModel) => {
            userLookupDictionary[resolvedUser.userId] = resolvedUser.displayName;
          });

          notes.rows.forEach(note => {
            const userId = note.UserInfoPublicId;
            if (!!userId) {
              note.displayName = userLookupDictionary[userId];
            }
          });
        });
      }
      return this.sortByDateDescending(notes);
    }
    return notes;
  };

  private sortByDateDescending(notes: SnapshotSection) {
    notes.rows.sort((a, b) => {
      const dateOne = new Date(a.EditDate).getTime();
      const dateTwo = new Date(b.EditDate).getTime();

      if (dateOne > dateTwo) {
        return -1;
      } else if (dateOne < dateTwo) {
        return 1;
      } else {
        return 0;
      }
    });

    return notes;
  }

  private checkOffsetOnScroll(elem: ElementRef<HTMLDivElement>) {
    let offset = false;
    if (elem) {
      const elemBoundingClientRect = elem.nativeElement.getBoundingClientRect();
      const elemTop = Math.ceil(elemBoundingClientRect.top);
      const containerTop = Math.ceil(this.device_info_container.nativeElement.getBoundingClientRect().top);
      const zoneEnd = Math.ceil(containerTop + this.highlightZoneHeight);

      offset = elemTop >= containerTop && elemTop < zoneEnd;
    }
    return offset;
  }

  private checkOffsetOnScrollUp(elem: ElementRef<HTMLDivElement>) {
    let offset = false;
    if (elem) {
      const elemBoundingClientRect = elem.nativeElement.getBoundingClientRect();
      const elemTop = Math.ceil(elemBoundingClientRect.top);
      const elemBottom = Math.ceil(elemBoundingClientRect.top + elemBoundingClientRect.height);

      const containerTop = Math.ceil(this.device_info_container.nativeElement.getBoundingClientRect().top);
      const zoneEnd = Math.ceil(containerTop + this.highlightZoneHeight);

      offset = elemBottom >= zoneEnd && elemTop <= zoneEnd;
    }
    return offset;
  }

  private calculateContainerHeight() {
    if (!this.isLoaded) {
      return;
    }

    const windowHeight: number =
      window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    this.highlightZoneHeight = this.calculatePercentage(windowHeight, 30); // 30% height of the snapshot container height
  }

  private calculatePercentage(originalValue: number, percentage: number) {
    return originalValue * (percentage / 100);
  }

  // This function is not used anywhere!!
  // private checkForReachingOutofRange() {
  //   if (!this.enableHighlight) {
  //     return;
  //   }
  //   if (this.elementInView === NavbarItems[NavbarItems.device_configurations]) {
  //     this.elementInView = NavbarItems[NavbarItems.device_details];
  //   } else if (this.elementInView === NavbarItems[NavbarItems.device_application]) {
  //     this.elementInView = NavbarItems[NavbarItems.device_configurations];
  //   } else if (this.elementInView === NavbarItems[NavbarItems.device_notes]) {
  //     this.elementInView = NavbarItems[NavbarItems.device_application];
  //   } else if (this.elementInView === NavbarItems[NavbarItems.device_security]) {
  //     this.elementInView = NavbarItems[NavbarItems.device_application];
  //   } else if (this.elementInView === NavbarItems[NavbarItems.device_content]) {
  //     this.elementInView = NavbarItems[NavbarItems.device_security];
  //   }
  // }

  private checkInfographicsSizing(elementRef: ElementRef<HTMLDivElement>) {
    if (elementRef.nativeElement.clientWidth < 530) {
      this.smallInfographics = true;
    } else {
      this.smallInfographics = false;
    }
  }
}
